"use client";

import React, { useEffect, useState } from "react";
import clsx from "clsx";
import { formatWithCommasWithDecimal, handleAddToCart } from "@/utils/helpers";
import { HomeProduct } from "@/types/home";
import { trackEvent } from "@/lib/gtag";
import { LoaderState } from "@/types/cart";
import { useAppDispatch, useAppSelector } from "@/hooks/useAppSelector";
import InfoPopover from "./InfoPopover";

interface MobilePriceCellProps {
  data: HomeProduct;
  redixData: { NDP_price: string | number; qty: number };
  selected: "ndp" | "sales";
  setSelected: (type: "ndp" | "sales") => void;
}

const MobilePriceCell: React.FC<MobilePriceCellProps> = ({
  data,
  redixData,
  selected,
  setSelected,
}) => {
  const ndp = Number(redixData?.NDP_price);
  const sales = Number(data?.Sales_Price);
  const salesQty = Number(data?.Bundle);
  const availableQty = redixData?.qty ?? 0;
  const bothPricesExist =
    !isNaN(ndp) &&
    !isNaN(sales) &&
    salesQty > 0 &&
    sales > 0 &&
    ndp > 0 &&
    ndp !== sales;

  const dispatch = useAppDispatch();
  const { phoneNumber } = useAppSelector((state) => state.auth);
  const [open, setOpen] = useState<LoaderState | null>(null);


  useEffect(() => {
    if (!bothPricesExist) {
      setSelected("sales");
    }
  }, [bothPricesExist, setSelected]);

  const handleOpen = (sku: string, type: string) => setOpen({ sku, type });
  const handleClose = () => setOpen(null);

  const handleSelect = (type: "ndp" | "sales") => {
    if (type === "sales") manageCartQty("inc");
    else manageCartQty("dec");
    setSelected(type);
  };

  const manageCartQty = (text: string) => {
    handleAddCarts(text);
  };

  const handleAddCarts = (type: string) => {
    const bundleQty = type === "inc" ? parseInt(data?.Bundle) || 1 : 1;

    if (availableQty <= 0 && type === "inc") {
      return;
    }

    if (type === "inc") {
      if (availableQty < bundleQty) {
        return;
      }

      trackEvent({
        action: "Increase Quantity",
        category: "Product",
        label: data?.SKU_ID,
      });

      handleOpen(data?.SKU_ID, type);

      handleAddToCart(
        dispatch,
        data?.SKU_ID,
        phoneNumber,
        bundleQty,
        selected,
        handleClose
      );
    }

    if (type === "dec") {
      if (availableQty <= 0) {
        return;
      }

      trackEvent({
        action: "Decrease Quantity",
        category: "Product",
        label: data?.SKU_ID,
      });

      handleOpen(data?.SKU_ID, type);

      handleAddToCart(
        dispatch,
        data?.SKU_ID,
        phoneNumber,
        bundleQty,
        selected,
        handleClose
      );
    }
  };
  return (
    <div>
      <div className="flex w-full gap-3 mt-4">
        {bothPricesExist && (
          <>
            {/* NDP Price */}
            <div
              className={clsx(
                "h-[55px] flex-1 p-2 rounded-xl border text-center text-sm cursor-pointer transition-all duration-300",
                selected === "ndp"
                  ? "bg-gradient-to-br from-blue-200 via-blue-100 to-white border-blue-500 text-blue-900 shadow-md"
                  : "bg-gradient-to-br from-gray-100 to-white border-gray-300 text-gray-500"
              )}
              onClick={() => handleSelect("ndp")}
            >
              <div className="font-semibold flex justify-center items-center">
                ₹{formatWithCommasWithDecimal(ndp)}
                <InfoPopover content="You can buy any required quantity. No MOQ is required." />
              </div>
              <div className="text-[10px]">(No MOQ)</div>
            </div>

            {/* Sales Price */}
            <div
              className={clsx(
                "h-[55px] flex-1 p-2 rounded-xl border text-center text-sm cursor-pointer transition-all duration-300",
                selected === "sales"
                  ? "bg-gradient-to-br from-green-200 via-green-100 to-white border-green-500 text-green-900 shadow-md"
                  : "bg-gradient-to-br from-gray-100 to-white border-gray-300 text-gray-500"
              )}
              onClick={() => handleSelect("sales")}
            >
              <div className="font-semibold flex justify-center items-center">
                ₹{formatWithCommasWithDecimal(sales)}
                <InfoPopover
                  content={`This price applies for standard carton size. MOQ is
                      required.`}
                />
              </div>
              <div className="text-[10px]">(MOQ - {salesQty} units)</div>
            </div>
          </>
        )}
        {!bothPricesExist && ndp && (
          <>
            {/* NDP Price (disabled) */}
            <div
              className={clsx(
                "h-[55px] flex-1 p-2 cursor-not-allowed rounded-xl border text-center text-sm transition-all duration-300",
                selected === "ndp"
                  ? "bg-gradient-to-br from-blue-200 via-blue-100 to-white border-blue-500 text-blue-900 shadow-md"
                  : "bg-gradient-to-br from-gray-100 to-white border-gray-300 text-gray-500"
              )}
              // onClick={() => handleSelect("ndp")}
            >
              <div className="font-semibold flex justify-center items-center">
                ₹{formatWithCommasWithDecimal(ndp)}
                <InfoPopover content="This item is available in carton pack only." />
              </div>
              <div className="text-[10px]">(No MOQ)</div>
            </div>

            {/* Sales tab (selected by default) */}
            <div
              className={clsx(
                "h-[55px] flex-1 p-2 rounded-xl border text-center text-sm cursor-pointer transition-all duration-300",
                selected === "sales"
                  ? "bg-gradient-to-br from-green-200 via-green-100 to-white border-green-500 text-green-900 shadow-md"
                  : "bg-gradient-to-br from-gray-100 to-white border-gray-300 text-gray-500"
              )}
              onClick={() => handleSelect("sales")}
            >
              <div className="font-semibold flex justify-center items-center">
                ₹{formatWithCommasWithDecimal(ndp)}
                <InfoPopover
                  content={`This item is available in carton packing only.`}
                />
              </div>
              <div className="text-[10px]">(MOQ - {salesQty || 1} units)</div>
            </div>
          </>
        )}
      </div>

      <div className="text-start mt-2 text-[11px] text-gray-500 font-medium">
        <strong>Note:</strong> Dealer Price (per unit, excl. GST)
      </div>
    </div>
  );
};

export default MobilePriceCell;
